import { Boxes, Fingerprint, GitBranch, Info, KeyRound, PackageSearch, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import type { PqcInventorySummary, SbomInventorySummary } from "../../api/types.js";
import { formatInteger } from "../../lib/format.js";
import type { InventorySelection } from "./inventory-selection.js";
import "./inventory-landscape.css";

function unique(values: readonly string[]): number {
  return new Set(values).size;
}

function compactPath(path: string): string {
  const parts = path.split("/");
  return parts.length > 2 ? parts.slice(0, 2).join("/") : (parts[0] ?? path);
}

function LandscapeBriefing({
  id,
  title,
  meaning,
  evidence,
  action,
  disabled = false,
  placement = "center",
  children,
}: {
  id: string;
  title: string;
  meaning: string;
  evidence: string;
  action: string;
  disabled?: boolean;
  placement?: "left" | "center" | "right";
  children: ReactNode;
}) {
  return (
    <div
      className={`inventory-landscape__briefed inventory-landscape__briefed--${placement}`}
      tabIndex={disabled ? 0 : undefined}
    >
      {children}
      <aside className="inventory-landscape__tooltip" id={id} role="tooltip">
        <header><Info aria-hidden="true" size={15} /><strong>{title}</strong></header>
        <dl>
          <div><dt>What it means</dt><dd>{meaning}</dd></div>
          <div><dt>Evidence here</dt><dd>{evidence}</dd></div>
          <div><dt>What to do</dt><dd>{action}</dd></div>
        </dl>
      </aside>
    </div>
  );
}

export function InventoryLandscape({
  sbom,
  cbom,
  onSelect,
}: {
  sbom: SbomInventorySummary;
  cbom: PqcInventorySummary;
  onSelect: (selection: InventorySelection) => void;
}) {
  const pqcPriority = cbom.rows.filter(
    (row) => row.algorithmClass === "quantum_vulnerable_public_key",
  );
  const hybrid = cbom.rows.filter((row) => row.algorithmClass === "hybrid");
  const unresolvedCrypto = cbom.rows.filter(
    (row) => row.algorithm === null || row.algorithmResolution.includes("dynamic"),
  );
  const transitive = sbom.rows.filter((row) => row.dependencyPath.length > 1);
  const manifests = unique(sbom.rows.map((row) => row.manifestPath));
  const sourceZones = [...new Set([
    ...cbom.rows.map((row) => compactPath(row.location.path)),
    ...sbom.rows.map((row) => compactPath(row.manifestPath)),
  ])].slice(0, 4);
  const selectCbom = (rows: typeof cbom.rows) => {
    const row = rows[0];
    if (row) onSelect({ kind: "cbom", row });
  };
  const selectSbom = (rows: typeof sbom.rows) => {
    const row = rows[0];
    if (row) onSelect({ kind: "sbom", row });
  };

  return (
    <section className="inventory-landscape" aria-labelledby="inventory-landscape-heading">
      <header>
        <div>
          <span className="page-kicker">01 · Map the evidence</span>
          <h2 id="inventory-landscape-heading">Repository supply-chain and cryptography landscape</h2>
          <p>Follow a sealed source surface into its software or crypto cohort, then open the exact evidence.</p>
        </div>
        <span className="inventory-landscape__proof">
          <ShieldCheck aria-hidden="true" size={15} /> Selected-scan evidence
        </span>
      </header>

      <div className="inventory-landscape__metrics" aria-label="Inventory evidence summary">
        <div><span>PQC migration items</span><strong>{formatInteger(pqcPriority.length)}</strong></div>
        <div><span>Software identities</span><strong>{formatInteger(sbom.uniqueComponents ?? sbom.rows.length)}</strong></div>
        <div><span>Transitive dependencies</span><strong>{formatInteger(transitive.length)}</strong></div>
        <div><span>Parsed manifests</span><strong>{formatInteger(manifests)}</strong></div>
      </div>

      <div className="inventory-landscape__map">
        <div className="inventory-landscape__column">
          <span className="inventory-landscape__column-label">Evidence zones</span>
          {sourceZones.map((zone, index) => {
            const cryptoRows = cbom.rows.filter((row) => compactPath(row.location.path) === zone);
            const softwareRows = sbom.rows.filter((row) => compactPath(row.manifestPath) === zone);
            const samplePath = cryptoRows[0]?.location.path ?? softwareRows[0]?.manifestPath ?? zone;
            return (
              <LandscapeBriefing
                id={`landscape-zone-${index}`}
                title={`${zone} evidence zone`}
                meaning="A repository area that contributed sealed SBOM or CBOM evidence to this scan."
                evidence={`${cryptoRows.length} crypto uses and ${softwareRows.length} software identities; example: ${samplePath}.`}
                action="Use the connected cohort to open the exact component or cryptographic call site."
                placement="left"
                key={zone}
              >
                <div className="inventory-landscape__node inventory-landscape__node--source" aria-describedby={`landscape-zone-${index}`} tabIndex={0}>
                  <GitBranch aria-hidden="true" size={18} />
                  <div><strong>{zone}</strong><small>Frozen repository surface {index + 1}</small></div>
                </div>
              </LandscapeBriefing>
            );
          })}
        </div>

        <div className="inventory-landscape__flow" aria-hidden="true"><i /><i /><i /></div>

        <div className="inventory-landscape__column">
          <span className="inventory-landscape__column-label">Evidence cohorts</span>
          <LandscapeBriefing id="landscape-pqc" title="Quantum-vulnerable public-key cohort" meaning="Classical RSA or elliptic-curve operations that a cryptographically relevant quantum computer could break." evidence={`${pqcPriority.length} source-bound uses are recorded in this selected scan.`} action="Open a row to distinguish ML-KEM key establishment from ML-DSA or SLH-DSA signature migration." disabled={!pqcPriority.length}>
            <button aria-describedby="landscape-pqc" type="button" className="inventory-landscape__node is-critical" onClick={() => selectCbom(pqcPriority)} disabled={!pqcPriority.length}>
              <KeyRound aria-hidden="true" size={18} /><div><strong>Quantum-vulnerable public key</strong><small>{pqcPriority.length} recorded crypto uses</small></div>
            </button>
          </LandscapeBriefing>
          <LandscapeBriefing id="landscape-transitive" title="Transitive dependency cohort" meaning="Packages reached through another dependency rather than declared directly by the application." evidence={`${transitive.length} nested identities are recorded in parsed lockfiles.`} action="Inspect the parent chain before choosing a direct upgrade, override, replacement, or acceptance decision." disabled={!transitive.length}>
            <button aria-describedby="landscape-transitive" type="button" className="inventory-landscape__node is-warning" onClick={() => selectSbom(transitive)} disabled={!transitive.length}>
              <PackageSearch aria-hidden="true" size={18} /><div><strong>Transitive dependency paths</strong><small>{transitive.length} nested lockfile identities</small></div>
            </button>
          </LandscapeBriefing>
          <LandscapeBriefing id="landscape-hybrid" title="Hybrid-ready cryptography cohort" meaning="Recorded cryptography that combines classical and post-quantum mechanisms during migration." evidence={`${hybrid.length} hybrid uses are recorded; zero means no hybrid proof was found, not that the system is unsafe.`} action="Validate protocol negotiation, downgrade resistance, interoperability, and rollback before relying on hybrid readiness." disabled={!hybrid.length}>
            <button aria-describedby="landscape-hybrid" type="button" className="inventory-landscape__node is-info" onClick={() => selectCbom(hybrid)} disabled={!hybrid.length}>
              <Fingerprint aria-hidden="true" size={18} /><div><strong>Hybrid-ready cryptography</strong><small>{hybrid.length} recorded crypto uses</small></div>
            </button>
          </LandscapeBriefing>
          <LandscapeBriefing id="landscape-unresolved" title="Unresolved cryptographic evidence" meaning="The scanner found a cryptographic API but could not prove the selected algorithm from static source." evidence={`${unresolvedCrypto.length} rows currently require runtime or owner-supplied proof.`} action="Record configuration, negotiation, key custody, or runtime evidence before making a migration claim." disabled={!unresolvedCrypto.length}>
            <button aria-describedby="landscape-unresolved" type="button" className="inventory-landscape__node is-muted" onClick={() => selectCbom(unresolvedCrypto)} disabled={!unresolvedCrypto.length}>
              <Boxes aria-hidden="true" size={18} /><div><strong>Unresolved crypto evidence</strong><small>{unresolvedCrypto.length} rows need owner proof</small></div>
            </button>
          </LandscapeBriefing>
        </div>

        <div className="inventory-landscape__flow inventory-landscape__flow--out" aria-hidden="true"><i /><i /><i /></div>

        <div className="inventory-landscape__column">
          <span className="inventory-landscape__column-label">Decision paths</span>
          <LandscapeBriefing id="landscape-migrate" title="Post-quantum migration decision" meaning="Key establishment generally maps to ML-KEM; digital signatures map to ML-DSA or SLH-DSA. They are not interchangeable." evidence={`${pqcPriority.length} classical public-key uses need purpose-specific review.`} action="Open the code-bound guidance, confirm protocol role and lifetime, then select and test the compatible standard." disabled={!pqcPriority.length} placement="right">
            <button aria-describedby="landscape-migrate" type="button" className="inventory-landscape__node inventory-landscape__node--decision" onClick={() => selectCbom(pqcPriority)} disabled={!pqcPriority.length}>
              <KeyRound aria-hidden="true" size={18} /><div><strong>Plan ML-KEM / signature migration</strong><small>Open code-bound crypto guidance</small></div>
            </button>
          </LandscapeBriefing>
          <LandscapeBriefing id="landscape-upgrade" title="Dependency remediation decision" meaning="A dependency path shows how a component enters the application; it does not prove exploitability by itself." evidence={`${transitive.length} transitive paths and ${sbom.rows.length} persisted component rows are available for review.`} action="Open the component evidence and correlate advisories, reachability, parent constraints, and a tested upgrade option." disabled={!sbom.rows.length} placement="right">
            <button aria-describedby="landscape-upgrade" type="button" className="inventory-landscape__node inventory-landscape__node--decision" onClick={() => selectSbom(transitive.length ? transitive : sbom.rows)} disabled={!sbom.rows.length}>
              <PackageSearch aria-hidden="true" size={18} /><div><strong>Review dependency upgrade paths</strong><small>Open parent-specific remediation evidence</small></div>
            </button>
          </LandscapeBriefing>
          <LandscapeBriefing id="landscape-assign" title="Evidence ownership decision" meaning="Unknown evidence is deliberately preserved instead of being converted into a clean or vulnerable claim." evidence={`${unresolvedCrypto.length} unresolved crypto rows are recorded in this scan.`} action="Assign an accountable owner and request the smallest missing proof needed to resolve the decision." disabled={!cbom.rows.length} placement="right">
            <button aria-describedby="landscape-assign" type="button" className="inventory-landscape__node inventory-landscape__node--decision" onClick={() => selectCbom(unresolvedCrypto.length ? unresolvedCrypto : cbom.rows)} disabled={!cbom.rows.length}>
              <ShieldCheck aria-hidden="true" size={18} /><div><strong>Assign missing evidence</strong><small>Preserve unknowns until proven</small></div>
            </button>
          </LandscapeBriefing>
        </div>
      </div>
    </section>
  );
}
